import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavService } from 'src/app/shared/navigation';
import { QBuiltService } from '../services/qbuilt-service';
import { User } from '../models/qbuilt-admin.models';
import { localStorageConstants } from 'src/app/shared/services';

import {
  PanelBodyComponent,
  PanelContainerComponent,
  PanelHeaderComponent,
} from 'src/app/shared/content-panels';
import { QBuiltConstants } from '../models/constants/qbuilt.constants';

type UserRow = User | { empty: true };

@Component({
  selector: 'qbuilt-user-admin-container',
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    PanelContainerComponent,
    PanelHeaderComponent,
    PanelBodyComponent,
  ],
  templateUrl: './user-admin-container.component.html',
  styleUrl: './user-admin-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAdminContainerComponent implements OnInit, OnDestroy {
  users: User[] = [];
  displayedColumns = [
    'userId',
    'firstName',
    'lastName',
    'email',
    'zones',
    'actions',
  ];
  plantNumber: string | null = null;
  plantName: string | null = null;

  breadcrumbTextHome: string = this.qbuiltConstants.breadcrumbTextHome;
  breadcrumbTextUserSettings: string = this.qbuiltConstants.breadCrumbUserSettings;


  readonly ZONE_PILL_LIMIT = 3;
  expandedZoneRows = new Set<string>();

  toggleZoneExpand(userId: string, event: Event): void {
    event.stopPropagation();
    if (this.expandedZoneRows.has(userId)) {
      this.expandedZoneRows.delete(userId);
    } else {
      this.expandedZoneRows.add(userId);
    }
    this.cdr.markForCheck();
  }

  visibleZones(user: User): any[] {
    const zones = user.zoneAssignments ?? [];
    return this.expandedZoneRows.has(user.id)
      ? zones
      : zones.slice(0, this.ZONE_PILL_LIMIT);
  }

  overflowCount(user: User): number {
    return (user.zoneAssignments?.length ?? 0) - this.ZONE_PILL_LIMIT;
  }

  isExpanded(userId: string): boolean {
    return this.expandedZoneRows.has(userId);
  }

  private emptyData: UserRow[] = [{ empty: true }];
  get tableData(): UserRow[] {
    return this.users.length ? this.users : this.emptyData;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private navService: NavService,
    private qbuiltService: QBuiltService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private qbuiltConstants: QBuiltConstants
  ) {}

  ngOnInit(): void {
    this.navService.setActive('CQMS');
    this.qbuiltService.setFacilityNumber(
      localStorageConstants.USER_FACILITIES_KEY,
    );
    this.qbuiltService.facilityNumber$
      .pipe(takeUntil(this.destroy$))
      .subscribe((plantNumber) => {
        if (plantNumber) {
          this.plantNumber = plantNumber;
          this.loadUsers(plantNumber);
        }
      });
  }

  navigateToDashboard(): void {
    this.router.navigate(['/cqms-dashboard']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editUser(user: User): void {
    this.router.navigate(['/users/edit-user', user.id]);
  }

  private loadUsers(plantNumber: string): void {
    this.qbuiltService
      .getAllUserZones(plantNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any[]) => {
          this.users = data.map((dto) => this.mapToUser(dto));
          this.plantName = data[0]?.PlantName ?? null;
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Failed to load users', err),
      });
  }

  private mapToUser(dto: any): User {
    return {
      id: String(dto.UserId),
      firstName: dto.FirstName,
      lastName: dto.LastName,
      email: dto.Email,
      plantNumber: dto.PlantNumber ?? '',
      plantName: dto.PlantName ?? '',
      zoneAssignments: (dto.Zones ?? []).map((z: any) => ({
        zoneId: String(z.ZoneId),
        zoneName: z.ZoneName,
        facilityName: dto.PlantName ?? '',
      })),
    };
  }
}
